import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { sportsApi, buildPosterUrl } from '@/services/sportsApi';
import { isEventLive, type SportsEvent } from '@/types/sports';

interface Props {
  event: SportsEvent;
}

const HOVER_PREFETCH_DELAY_MS = 150;

export function formatTimePill(event: SportsEvent, t: TFunction, now: number = Date.now()): string {
  if (event.sport === '247') return '24/7';
  if (isEventLive(event)) {
    // Show elapsed minutes since kick-off so the user can pick between
    // multiple live games. Always-live (no real startsAt) renders bare LIVE.
    if (Number.isFinite(event.startsAt) && event.startsAt > 0 && now > event.startsAt) {
      const elapsedMins = Math.floor((now - event.startsAt) / 60_000);
      return t('sports.pill.liveElapsed', { minutes: elapsedMins });
    }
    return t('sports.pill.live');
  }
  const start = event.startsAt;
  if (!Number.isFinite(start) || start === 0) return '';
  const diff = start - now;
  if (diff < 0) return t('sports.pill.ended');
  const mins = Math.round(diff / 60_000);
  if (mins < 60) return t('sports.pill.inMinutes', { minutes: mins });
  if (mins < 24 * 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? t('sports.pill.inHours', { hours: h }) : t('sports.pill.inHoursMinutes', { hours: h, minutes: m });
  }
  const d = new Date(start);
  return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

export function SportsEventCard({ event }: Props): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hoverTimerRef = useRef<number | null>(null);
  const prefetchControllerRef = useRef<AbortController | null>(null);
  const [posterFailed, setPosterFailed] = useState(false);

  const providersOnCard = Array.from(new Set(event.sources.map((s) => s.provider)));

  function onMouseEnter(): void {
    if (hoverTimerRef.current !== null) return;
    hoverTimerRef.current = window.setTimeout(() => {
      const controller = new AbortController();
      prefetchControllerRef.current = controller;
      void sportsApi.prefetch(event.id, undefined, controller.signal);
    }, HOVER_PREFETCH_DELAY_MS);
  }

  function onMouseLeave(): void {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (prefetchControllerRef.current) {
      prefetchControllerRef.current.abort();
      prefetchControllerRef.current = null;
    }
  }

  return (
    <button
      type="button"
      className="sports-card"
      onClick={() => navigate(`/watch/sports/${encodeURIComponent(event.id)}`)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={t('sports.watchAria', { title: event.title })}
    >
      <div className="sports-card__poster">
        {event.posterUrl !== null && !posterFailed ? (
          <img
            src={buildPosterUrl(event.id)}
            alt=""
            loading="lazy"
            onError={() => setPosterFailed(true)}
          />
        ) : (
          <div className="sports-card__poster-placeholder" />
        )}
        <span className={`time-pill${isEventLive(event) ? ' time-pill--live' : ''}`}>
          {formatTimePill(event, t)}
        </span>
      </div>
      <div className="sports-card__title">{event.title}</div>
      {event.league && <div className="sports-card__league">{event.league}</div>}
      <div className="sports-card__sources" aria-label={t('sports.sourcesLabel')}>
        {providersOnCard.map((p) => (
          <span key={p} className={`source-dot source-dot--${p}`}>
            {p === 'streamed' ? 'S' : 'P'}
          </span>
        ))}
      </div>
    </button>
  );
}
